import {
    isPlayerAction,
    isRemovePlayerEvent,
    PlayerId,
    Projection,
    SharedGameState,
    useGameStore,
} from '@glark-newco/game-library';
import {isAssignPlayerNodePayload, isTransferRingEvent, RingId} from '../SupplyChainPayloadTypes';

export interface RingInfo {
    ringId: RingId;
    firstOwner: PlayerId;
    currentOwner: PlayerId;
    transferCount: number;
}

export type RingStatisticsMap = Record<RingId, RingInfo>;

export interface RingStatisticsSlice extends SharedGameState {
    ringStatisticsMap?: RingStatisticsMap;
}

function ringInfo(ringId: RingId, playerId: PlayerId) {
    return {
        ringId,
        currentOwner: playerId,
        firstOwner: playerId,
        transferCount: 0,
    };
}

export const ringStatisticsProjection: Projection<RingStatisticsSlice> = {
    name: 'ringStatisticsProjection',
    project: (state, newEvents) => {
        const infoMap: RingStatisticsMap = {...(state.ringStatisticsMap ?? {})};
        let modified = false;

        newEvents.forEach(ev => {
            if (isPlayerAction(ev) && isAssignPlayerNodePayload(ev.payload)) {
                const playerPayload = ev.payload;
                const rings = playerPayload.rings ?? [];
                rings.forEach(r => {
                    infoMap[r.ringId] =  ringInfo(r.ringId, playerPayload.playerId);
                });
                modified = true;
            }
            if (isPlayerAction(ev) && isTransferRingEvent(ev.payload)) {
                const payload = ev.payload;
                if (!infoMap[payload.ringId]) {
                    console.warn(`ringStatisticsProjection: ignoring transfer for invalid ring "${payload.ringId}" as it wasn't assigned to any players.`);
                } else {
                    infoMap[payload.ringId] = {
                        ...infoMap[payload.ringId],
                        currentOwner: payload.recipientPlayerId,
                        transferCount: infoMap[payload.ringId].transferCount + 1,
                    };
                    modified = true;
                }
            }
            if (isRemovePlayerEvent(ev) && ev.replacementId) {
                const removedId = ev.removePlayerId;
                const replacementId = ev.replacementId;

                // Update ownership for rings where the removed player is the current owner
                Object.entries(infoMap).forEach(([rid, info]) => {
                    if (info.firstOwner === removedId) {
                        infoMap[rid] = {...infoMap[rid], firstOwner: replacementId};
                        modified = true;
                    }
                    if (info.currentOwner === removedId) {
                        infoMap[rid] = {...infoMap[rid], currentOwner: replacementId};
                        modified = true;
                    }
                });
                return;
            }
        });

        return modified ? {ringStatisticsMap: infoMap} : {};
    },
    reset: () => ({ringStatisticsMap: {}}),
};

// =============================================
// = Hooks.  Zustand advice - perform any refinement in the selector, to avoid zustand triggering a re-render
// =============================================

const emptyRingMap: RingStatisticsMap = {};   // stable reference to prevent react-render churn

const emptyRingState = {
    ringId: undefined,
    transferCount: 0,
    firstOwner: undefined,
    currentOwner: undefined,
};

export const useMostActiveRing = () =>  useGameStore((s: RingStatisticsSlice) => {
    // Logic inside the selector prevents the store triggering this selector if the derived result does not change
    const map = s.ringStatisticsMap ?? emptyRingMap;
    const sorted = Object.entries(map).sort(
        ([, a], [, b]) => b.transferCount - a.transferCount,
    );
    return sorted.length ? sorted[0][1] : emptyRingState;
});

/** Average number of transfers per ring (including rings that have never transferred). */
export const useAverageTransfersPerRing = () => useGameStore((s: RingStatisticsSlice) => {
    // Logic inside the selector prevents the store triggering this selector if the derived result does not change
    const ringStatsMap = s.ringStatisticsMap ?? emptyRingMap;
    const totalTransfers = Object.values(ringStatsMap).reduce((sum, c) => sum + c.transferCount, 0);
    const totalRings = Object.keys(ringStatsMap).length;
    return totalRings === 0 ? 0 : totalTransfers / totalRings;
});

